/**
 * caseTypeConfig.js
 *
 * Single source of truth for every case-type's field definitions.
 * Drives:  CaseForm rendering, AI prompt building,
 *          missing-field detection, follow-up email generation.
 *
 * Field shape:
 *   key       – camelCase identifier used in state & API payloads
 *   label     – human-readable label shown in the form
 *   type      – "text" | "select" | "date" | "textarea"
 *   options   – (select only) array of allowed values
 *   aiPrompt  – description sent to the AI for extraction
 *   required  – whether it's required for case creation (default false)
 */

// ────────────────────────────────────────────
// Case Type list (used in dropdowns, AI prompt)
// ────────────────────────────────────────────
export const CASE_TYPE_OPTIONS = [
  "Alarm System Quoted and Added",
  "Cancellation Personal Lines",
  "Coverage Changes to HOI Policy",
  "Insured Passed Away",
  "Mailing/Garaging Address Update",
  "Midterm Add Solar Panels",
  "Midterm New Roof",
  "Mortgage Paid Off",
  "Mortgage Update",
  "Moved Property into LLC",
  "Original Occupancy Change",
  "Other",
  "Pool Addition",
  "Update Name",
  "Vehicle Change",
  "New Boat Purchase",
];

// ────────────────────────────────────────────
// Common fields (always visible regardless of case type)
// "subject", "contactId", "policyId", "caseType", "priority", "description"
// are rendered separately (hard-coded in CaseForm top section).
//
// These "standard header" fields from the Excel appear after the
// hard-coded ones, in every case type.
// ────────────────────────────────────────────
export const STANDARD_HEADER_FIELDS = [
  { key: "insuredFirstName", label: "Insured First Name", type: "text",
    aiPrompt: "Insured first name" },
  { key: "insuredLastName", label: "Insured Last Name", type: "text",
    aiPrompt: "Insured last name" },
  { key: "policyNumbers", label: "Policy Number(s)", sfApiNameByChildObject: { Case_Hoi_1__c: "policy_num__c", Case_Gen_1__c: "policy_num__c", Case_Aut_1__c: "Auto_Policy_Number__c" }, type: "text",
    aiPrompt: "Policy number(s) mentioned" },
  { key: "requestedEffectiveDate", label: "Requested Effective Date of Change", sfApiNameByChildObject: { Case_Hoi_1__c: "eff_chng_dt__c", Case_Gen_1__c: "eff_date_chg__c", Case_Aut_1__c: "chg_eff_dt__c" }, type: "date",
    aiPrompt: "Requested effective date of the change (YYYY-MM-DD format, empty string if not mentioned)" },
];

// ────────────────────────────────────────────
// Salesforce child object mapping per case type
// ────────────────────────────────────────────
export const CHILD_OBJECT_MAP = {
  "Alarm System Quoted and Added": "Case_Hoi_1__c",
  "Cancellation Personal Lines": "Case_Hoi_1__c",
  "Coverage Changes to HOI Policy": "Case_Hoi_1__c",
  "Midterm Add Solar Panels": "Case_Hoi_1__c",
  "Midterm New Roof": "Case_Hoi_1__c",
  "Mortgage Paid Off": "Case_Hoi_1__c",
  "Moved Property into LLC": "Case_Hoi_1__c",
  "Original Occupancy Change": "Case_Hoi_1__c",
  "Update Name": "Case_Hoi_1__c",

  "Insured Passed Away": "Case_Gen_1__c",
  "Mailing/Garaging Address Update": "Case_Gen_1__c",
  "Mortgage Update": "Case_Gen_1__c",
  "Pool Addition": "Case_Gen_1__c",
  "New Boat Purchase": "Case_Gen_1__c",

  "Vehicle Change": "Case_Aut_1__c",
};

/**
 * Get the Salesforce child object API name for a given caseType.
 * Returns null for "Other" or unknown types.
 */
export const getChildObjectName = (caseType) => {
  return CHILD_OBJECT_MAP[caseType] || null;
};

/**
 * Resolve Salesforce mapping metadata for a frontend field definition.
 * Returns null when the field is not present in Salesforce and should be
 * skipped from case-creation payloads. The original frontend key remains
 * available everywhere else, including follow-up email generation.
 */
export const getSalesforceMappingForField = (field, caseType) => {
  const childObjectName = getChildObjectName(caseType);

  if (field.sfApiNameByChildObject && childObjectName) {
    const apiName = field.sfApiNameByChildObject[childObjectName];
    if (apiName) {
      // Special case: Auto_Policy_Number__c is a field on the parent Case object, not the Case_Aut_1__c child object
      const objectName = apiName === "Auto_Policy_Number__c" ? "Case" : childObjectName;
      return { objectName, apiName };
    }
  }

  if (field.sfApiName) {
    return {
      objectName: field.sfObjectName || childObjectName || "Case",
      apiName: field.sfApiName,
    };
  }

  return null;
};

// ────────────────────────────────────────────
// Per-case-type field definitions
// ────────────────────────────────────────────
export const CASE_TYPE_FIELDS = {

  // ─── 1. Alarm System Quoted and Added ───
  "Alarm System Quoted and Added": {
    sections: [
      {
        title: "Alarm System Details",
        fields: [
          { key: "alarmType", label: "Type of Alarm", sfObjectName: "Case_Hoi_1__c", sfApiName: "alarm_type__c", type: "select",
            options: ["Burglar", "Fire", "Both"],
            aiPrompt: "Type of Alarm (Burglar / Fire / Both)" },
          { key: "centrallyMonitored", label: "Is the Alarm Centrally Monitored?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Is the Alarm Centrally Monitored? (Yes/No)" },
        ],
      },
      {
        title: "Alarm Components / Coverage",
        fields: [
          { key: "sprinklerInstalled", label: "Sprinkler System Installed?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Sprinkler System Installed? (Yes/No)" },
          { key: "sprinklerCoverage", label: "Sprinkler Coverage", type: "select",
            options: ["Partial", "Full"],
            aiPrompt: "Sprinkler Coverage (Partial/Full)" },
          { key: "proofOfInstallation", label: "Proof of Installation Documentation Received?", sfObjectName: "Case", sfApiName: "Supporting_Documentation_Received__c", type: "select", sfBoolean: true,
            options: ["Yes", "No"],
            aiPrompt: "Proof of Installation Documentation Received? (Yes/No)" },
          { key: "certificateCompleted", label: "Certificate of Alarm Form Completed?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Certificate of Alarm Form Completed? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 2. Cancellation Personal Lines ───
  "Cancellation Personal Lines": {
    sections: [
      {
        title: "Cancellation Basics",
        fields: [
          { key: "policyBeingCancelled", label: "Policy Number(s) Being Cancelled", sfObjectName: "Case_Hoi_1__c", sfApiName: "policy_num__c", type: "text",
            aiPrompt: "Policy number(s) being cancelled" },
          { key: "cancellationDate", label: "Requested Cancellation Effective Date", sfObjectName: "Case", sfApiName: "Cancellation_Effective_Date__c", type: "date",
            aiPrompt: "Requested cancellation effective date (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "cancellationReason", label: "Reason for Cancellation", sfObjectName: "Case", sfApiName: "Cancellation_Reason__c", type: "select",
            options: [
              "Sold Home",
              "Found Coverage Elsewhere",
              "Paid off Mortgage",
              "Other",
              "Sold Vehicle"
            ],
            aiPrompt: "Reason for cancellation. Must be one of the following exact values: 'Sold Home', 'Found Coverage Elsewhere', 'Paid off Mortgage', 'Other', 'Sold Vehicle'." },
          { key: "replacementCoverage", label: "Replacement Coverage in Place?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Replacement Coverage in Place? (Yes/No)" },
          { key: "newCarrierName", label: "New Carrier Name (if replaced)", sfObjectName: "Case", sfApiName: "New_Carrier_Name__c", type: "text",
            aiPrompt: "New carrier name if replacement coverage exists" },
          { key: "newPolicyEffectiveDate", label: "New Policy Effective Date", sfObjectName: "Case_Hoi_1__c", sfApiName: "policy_eff_date__c", type: "date",
            aiPrompt: "New policy effective date (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "proofOfReplacement", label: "Proof of Replacement Coverage Received?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Proof of Replacement Coverage Received? (Yes/No)" },
          { key: "propertySold", label: "Property Sold?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Property Sold? (Yes/No)" },
          { key: "closingSaleDate", label: "Closing/Sale Date", sfObjectName: "Case_Hoi_1__c", sfApiName: "closing_date__c", type: "date",
            aiPrompt: "Closing or sale date (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "billOfSaleProvided", label: "Bill of Sale Provided?", sfObjectName: "Case", sfApiName: "Bill_of_Sale_Received__c", type: "select", sfBoolean: true,
            options: ["Yes", "No"],
            aiPrompt: "Bill of Sale Provided? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 3. Coverage Changes to HOI Policy ───
  "Coverage Changes to HOI Policy": {
    sections: [
      {
        title: "Requested Coverage Changes",
        fields: [
          { key: "coverageChanges", label: "Coverage(s) Being Increased / Decreased / Added / Removed", type: "text",
            aiPrompt: "Coverage(s) being increased, decreased, added, or removed" },
          { key: "newCoverageAmount", label: "Requested New Coverage Amount", type: "text",
            aiPrompt: "Requested new coverage amount" },
          { key: "newAopDeductible", label: "Requested New AOP Deductible ($)", sfObjectName: "Case_Hoi_1__c", sfApiName: "All_Other_Perils_Deductible_if_changed__c", type: "text",
            aiPrompt: "Requested new AOP deductible amount ($)" },
          { key: "newHurricaneDeductible", label: "Requested New Hurricane Deductible (% or $)", type: "text",
            aiPrompt: "Requested new hurricane deductible (% or $)" },
          { key: "endorsementsToAdd", label: "Endorsements to Add", sfObjectName: "Case", sfApiName: "WT_Endorsement_URL__c", type: "text",
            aiPrompt: "Endorsements to add (Scheduled Personal Property / Identity Theft / Equipment Breakdown / Service Line / Water Backup)" },
          { key: "endorsementsToRemove", label: "Endorsements to Remove", sfObjectName: "Case_Hoi_1__c", sfApiName: "endorse_form__c", type: "text",
            aiPrompt: "Endorsements to remove" },
          { key: "coverageChangeReason", label: "Reason for Coverage Change", sfObjectName: "Case_Hoi_1__c", sfApiName: "Other_Coverage_change__c", type: "select",
            options: ["Renovation", "Appraisal", "Lender Requirement", "Personal Preference"],
            aiPrompt: "Reason for coverage change (Renovation / Appraisal / Lender Requirement / Personal Preference)" },
        ],
      },
    ],
  },

  // ─── 4. Insured Passed Away ───
  "Insured Passed Away": {
    sections: [
      {
        title: "Deceased Insured Information",
        fields: [
          { key: "deceasedName", label: "Full Name of Deceased", sfObjectName: "Case_Gen_1__c", sfApiName: "Name_of_the_deceased__c", type: "text",
            aiPrompt: "Full name of the deceased insured" },
          { key: "dateOfDeath", label: "Date of Death", type: "date",
            aiPrompt: "Date of death (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "deathCertificateReceived", label: "Death Certificate Received?", sfObjectName: "Case", sfApiName: "Death_Certificate_Received__c", type: "select", sfBoolean: true,
            options: ["Yes", "No"],
            aiPrompt: "Death Certificate Received? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 5. Mailing/Garaging Address Update ───
  "Mailing/Garaging Address Update": {
    sections: [
      {
        title: "Address Change Type",
        fields: [
          { key: "addressChangeType", label: "Type of Change", sfObjectName: "Case", sfApiName: "WT_ChangeType__c", type: "select",
            options: ["Mailing Only", "Garaging Only", "Both"],
            aiPrompt: "Type of address change (Mailing Only / Garaging Only / Both)" },
          { key: "policiesAffected", label: "Policies Affected", sfObjectName: "Case_Gen_1__c", sfApiName: "Policies_Impacted__c", type: "select", sfMultiSelect: true,
            options: ["Homeowners", "Flood", "Auto", "Boat", "Specialty", "Umbrella"],
            aiPrompt: "Policies affected. Select all that apply from: Homeowners, Flood, Auto, Boat, Specialty, Umbrella" },
          { key: "addressChangeEffectiveDate", label: "Effective Date of Address Change", sfObjectName: "Case_Gen_1__c", sfApiName: "eff_date_chg__c", type: "date",
            aiPrompt: "Effective date of address change (YYYY-MM-DD, empty string if not mentioned)" },
        ],
      },
      {
        title: "New Address Information",
        fields: [
          { key: "newMailingAddress", label: "New Mailing Address (Street, City, State, ZIP)", sfObjectName: "Case_Gen_1__c", sfApiName: "new_mail_addr__c", type: "text",
            aiPrompt: "New mailing address (Street, City, State, ZIP)" },
          { key: "newGaragingAddress", label: "New Garaging Address (Street, City, State, ZIP)", sfObjectName: "Case_Gen_1__c", sfApiName: "new_garage_addr__c", type: "text",
            aiPrompt: "New garaging address (Street, City, State, ZIP)" },
          { key: "newAddressInFlorida", label: "Is New Address in Florida?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Is new address in Florida? (Yes/No)" },
          { key: "outOfStateResidence", label: "If Out of State - New State of Residence", type: "text",
            aiPrompt: "If out of state, new state of residence" },
        ],
      },
      {
        title: "Garaging-Specific (Auto)",
        fields: [
          { key: "vehiclesAffected", label: "Vehicles Affected (Year/Make/Model/VIN)", sfObjectName: "Case", sfApiName: "Vehicle__c", type: "text",
            aiPrompt: "Vehicles affected by garaging change (Year/Make/Model/VIN)" },
        ],
      },
      {
        title: "Property-Specific (Home)",
        fields: [
          { key: "insuredMoving", label: "Is Insured Moving Residence?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Is insured moving residence? (Yes/No)" },
          { key: "oldHomeStatus", label: "Is Old Home Being Sold / Rented / Kept?", type: "text",
            aiPrompt: "Is old home being sold, rented, or kept?" },
          { key: "needQuoteNewHome", label: "Need to Quote New Home?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Need to quote new home? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 6. Midterm Add Solar Panels ───
  "Midterm Add Solar Panels": {
    sections: [
      {
        title: "Solar Installation Details",
        fields: [
          { key: "solarInstallDate", label: "Date of Installation / Anticipated Install Date", type: "date",
            aiPrompt: "Date of solar installation or anticipated install date (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "installerName", label: "Installer / Contractor Name", type: "text",
            aiPrompt: "Installer or contractor name" },
          { key: "permitNumber", label: "Permit Number (FL Required)", type: "text",
            aiPrompt: "Permit number (FL required)" },
          { key: "inspectionPassed", label: "Inspection Passed?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Inspection Passed? (Yes/No)" },
        ],
      },
      {
        title: "System Specifications",
        fields: [
          { key: "totalSystemCost", label: "Total System Cost ($)", type: "text",
            aiPrompt: "Total system cost ($)" },
        ],
      },
      {
        title: "Documentation",
        fields: [
          { key: "solarPhotosProvided", label: "Photos of Installation Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Photos of installation provided? (Yes/No)" },
          { key: "solarInvoiceProvided", label: "Invoice / Receipt Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Invoice or receipt provided? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 7. Midterm New Roof ───
  "Midterm New Roof": {
    sections: [
      {
        title: "Roof Replacement Basics",
        fields: [
          { key: "roofReplacementDate", label: "Date of Roof Replacement / Anticipated Date", type: "date",
            aiPrompt: "Date of roof replacement or anticipated date (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "replacementReason", label: "Reason for Replacement", type: "select",
            options: ["Age", "Hurricane", "Hail", "Insurance Claim", "Real Estate"],
            aiPrompt: "Reason for replacement (Age / Hurricane / Hail / Insurance Claim / Real Estate)" },
          { key: "fullOrPartial", label: "Full Replacement or Partial?", type: "select",
            options: ["Full", "Partial"],
            aiPrompt: "Full replacement or partial? (Full/Partial)" },
          { key: "partialSectionsReplaced", label: "If Partial - Sections Replaced", type: "text",
            aiPrompt: "If partial replacement, which sections were replaced" },
        ],
      },
      {
        title: "Roof Specifications",
        fields: [
          { key: "newRoofMaterial", label: "New Roof Material", type: "select",
            options: ["Shingle", "Tile", "Metal", "Flat", "Other"],
            aiPrompt: "New roof material (Shingle / Tile / Metal / Flat / Other)" },
        ],
      },
      {
        title: "Florida Wind Mitigation",
        fields: [
          { key: "windMitigationCompleted", label: "New Wind Mitigation Inspection Completed?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "New wind mitigation inspection completed? (Yes/No)" },
        ],
      },
      {
        title: "Documentation Checklist",
        fields: [
          { key: "permitCertificateProvided", label: "Final Permit / Inspection Certificate Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Final permit or inspection certificate provided? (Yes/No)" },
          { key: "windMitigationFormProvided", label: "New Wind Mitigation Form Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "New wind mitigation form provided? (Yes/No)" },
          { key: "contractorInvoiceProvided", label: "Contractor Invoice Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Contractor invoice provided? (Yes/No)" },
          { key: "roofPhotosProvided", label: "Photos of New Roof Provided?", sfObjectName: "Case_Hoi_1__c", sfApiName: "New_Roof_Photos_Received__c", type: "select", sfBoolean: true,
            options: ["Yes", "No"],
            aiPrompt: "Photos of new roof provided? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 8. Mortgage Paid Off ───
  "Mortgage Paid Off": {
    sections: [
      {
        title: "Payoff Information",
        fields: [
          { key: "mortgagePaidOffDate", label: "Date Mortgage Paid Off", type: "date",
            aiPrompt: "Date mortgage was paid off (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "mortgageeLenderRemoved", label: "Mortgagee/Lender Being Removed", sfObjectName: "Case_Hoi_1__c", sfApiName: "mortgage_rmv__c", type: "text",
            aiPrompt: "Mortgagee or lender being removed" },
          { key: "loanNumberRemoved", label: "Loan Number Being Removed", sfObjectName: "Case", sfApiName: "WT_LoanNumber__c", type: "text",
            aiPrompt: "Loan number being removed" },
          { key: "payoffStatementProvided", label: "Payoff Statement / Satisfaction of Mortgage Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Payoff statement or satisfaction of mortgage provided? (Yes/No)" },
        ],
      },
      {
        title: "Coverage Review Trigger",
        fields: [
          { key: "wantsCoverageReview", label: "Insured Wants Coverage Review Now That Mortgage Is Off?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Insured wants coverage review now that mortgage is off? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 9. Mortgage Update ───
  "Mortgage Update": {
    sections: [
      {
        title: "Type of Mortgage Update",
        fields: [
          { key: "mortgageChangeType", label: "Type of Change", sfObjectName: "Case", sfApiName: "WT_ChangeType__c", type: "select",
            options: ["New Mortgage", "Refinance", "Add Mortgagee", "Change Mortgagee", "Update Loan Number", "Update Mortgagee Address"],
            aiPrompt: "Type of mortgage change (New Mortgage / Refinance / Add Mortgagee / Change Mortgagee / Update Loan Number / Update Mortgagee Address)" },
          { key: "mortgageChangeEffectiveDate", label: "Effective Date of Change", sfObjectName: "Case_Gen_1__c", sfApiName: "eff_date_chg__c", type: "date",
            aiPrompt: "Effective date of mortgage change (YYYY-MM-DD, empty string if not mentioned)" },
        ],
      },
      {
        title: "New Mortgagee Information",
        fields: [
          { key: "newMortgageeName", label: "New Mortgagee Name (Exact Legal Name)", sfObjectName: "Case", sfApiName: "WT_MortgageeName__c", type: "text",
            aiPrompt: "New mortgagee name (exact legal name)" },
          { key: "newLoanNumber", label: "New Loan Number", sfObjectName: "Case", sfApiName: "WT_LoanNumber__c", type: "text",
            aiPrompt: "New loan number" },
          { key: "newMortgageeAddress", label: "New Mortgagee Mailing Address", sfObjectName: "Case_Gen_1__c", sfApiName: "new_mail_addr__c", type: "text",
            aiPrompt: "New mortgagee mailing address" },
          { key: "newMortgageePosition", label: "New Mortgagee Position", type: "select",
            options: ["1st", "2nd"],
            aiPrompt: "New mortgagee position (1st / 2nd)" },
        ],
      },
    ],
  },

  // ─── 10. Moved Property into LLC ───
  "Moved Property into LLC": {
    sections: [
      {
        title: "LLC / Entity Information",
        fields: [
          { key: "llcLegalName", label: "Full Legal Name of LLC", sfObjectName: "Case_Hoi_1__c", sfApiName: "Legal_name_of_LLC_or_Corp__c", type: "text",
            aiPrompt: "Full legal name of the LLC" },
          { key: "llcStateOfFormation", label: "LLC State of Formation", type: "text",
            aiPrompt: "LLC state of formation" },
          { key: "llcDocumentNumberEin", label: "LLC Florida Document Number / EIN", type: "text",
            aiPrompt: "LLC Florida document number or EIN" },
          { key: "llcDateFormed", label: "Date LLC Was Formed", type: "date",
            aiPrompt: "Date LLC was formed (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "llcMailingAddress", label: "LLC Mailing Address", sfObjectName: "Case_Hoi_1__c", sfApiName: "new_mail_addr__c", type: "text",
            aiPrompt: "LLC mailing address" },
          { key: "occupancyChangeConfirm", label: "Confirm if a Change in Occupancy", type: "text",
            aiPrompt: "Confirm if there is a change in occupancy" },
        ],
      },
      {
        title: "LLC Members / Managers",
        fields: [
          { key: "llcMembers", label: "Names of All LLC Members", type: "text",
            aiPrompt: "Names of all LLC members" },
        ],
      },
    ],
  },

  // ─── 11. Original Occupancy Change ───
  "Original Occupancy Change": {
    sections: [
      {
        title: "Occupancy Change Details",
        fields: [
          { key: "propertyAddress", label: "Property Address", sfObjectName: "Case", sfApiName: "New_Property_Address__c", type: "text",
            aiPrompt: "Property address" },
          { key: "newOccupancyType", label: "New Occupancy Type", sfObjectName: "Case_Hoi_1__c", sfApiName: "New_Occupancy__c", type: "text",
            aiPrompt: "New occupancy type" },
          { key: "occupancyChangeEffectiveDate", label: "Effective Date of Occupancy Change", sfObjectName: "Case_Hoi_1__c", sfApiName: "eff_chng_dt__c", type: "date",
            aiPrompt: "Effective date of occupancy change (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "occupancyChangeReason", label: "Reason for Change", type: "text",
            aiPrompt: "Reason for occupancy change" },
        ],
      },
      {
        title: "Primary to Secondary / Seasonal",
        fields: [
          { key: "newPrimaryAddress", label: "New Primary Residence Address", type: "text",
            aiPrompt: "New primary residence address" },
          { key: "monthsOccupied", label: "How Many Months/Year Will Property Be Occupied?", type: "text",
            aiPrompt: "How many months per year will the property be occupied?" },
          { key: "monthsVacant", label: "Months Property Will Be Vacant", type: "text",
            aiPrompt: "Months the property will be vacant" },
          { key: "propertyCheckedRegularly", label: "Will Property Be Checked Regularly?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Will property be checked regularly? (Yes/No)" },
          { key: "whoChecksProperty", label: "Who Checks the Property", type: "select",
            options: ["Family", "Neighbor", "Property Manager"],
            aiPrompt: "Who checks the property (Family / Neighbor / Property Manager)" },
          { key: "securitySystemActive", label: "Security System Active?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Security system active? (Yes/No)" },
        ],
      },
      {
        title: "Primary/Secondary to Rental",
        fields: [
          { key: "rentalType", label: "Type of Rental", type: "select",
            options: ["Long-Term Annual", "Short-Term", "Seasonal", "Airbnb-VRBO"],
            aiPrompt: "Type of rental (Long-Term Annual / Short-Term / Seasonal / Airbnb-VRBO)" },
          { key: "rentalStartDate", label: "Rental Start Date", type: "date",
            aiPrompt: "Rental start date (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "leaseTerm", label: "Lease Term", sfObjectName: "Case_Hoi_1__c", sfApiName: "If_rental_lease_term__c", type: "text",
            aiPrompt: "Lease term" },
          { key: "rentalIncome", label: "Monthly/Annual Rental Income ($)", type: "text",
            aiPrompt: "Monthly or annual rental income ($)" },
        ],
      },
    ],
  },

  // ─── 12. Pool Addition ───
  "Pool Addition": {
    sections: [
      {
        title: "Pool Installation Details",
        fields: [
          { key: "poolInstallDate", label: "Date Pool Installed / Anticipated Install Date", type: "date",
            aiPrompt: "Date pool was installed or anticipated install date (YYYY-MM-DD, empty string if not mentioned)" },
        ],
      },
      {
        title: "Pool Type / Specifications",
        fields: [
          { key: "poolType", label: "Pool Type", sfObjectName: "Case_Gen_1__c", sfApiName: "pool_Type__c", type: "select",
            options: ["In-ground", "Above-ground", "Spa/Hot Tub", "Combination (Pool & Spa)"],
            aiPrompt: "Pool type (In-ground / Above-ground / Spa/Hot Tub / Combination (Pool & Spa))" },
          { key: "poolCost", label: "Total Cost of Pool ($)", type: "text",
            aiPrompt: "Total cost of pool ($)" },
        ],
      },
      {
        title: "Safety Features (Critical for Underwriting)",
        fields: [
          { key: "fenceAroundPool", label: "Fence Around Pool?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Fence around pool? (Yes/No)" },
          { key: "divingBoard", label: "Diving Board?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Diving board? (Yes/No)" },
          { key: "slide", label: "Slide?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Slide? (Yes/No)" },
          { key: "screenedEnclosure", label: "Screened Enclosure?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Screened enclosure? (Yes/No)" },
        ],
      },
      {
        title: "Documentation Checklist",
        fields: [
          { key: "poolPhotosProvided", label: "Photos of Pool and Safety Features Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Photos of pool and safety features provided? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 13. Update Name ───
  "Update Name": {
    sections: [
      {
        title: "Reason for Name Change",
        fields: [
          { key: "nameChangeReason", label: "Reason", sfObjectName: "Case_Hoi_1__c", sfApiName: "reason_update__c", type: "select",
            options: ["Marriage", "Divorce", "Death"],
            aiPrompt: "Reason for name change (Marriage / Divorce / Death)" },
          { key: "nameChangeEffectiveDate", label: "Effective Date of Name Change", sfObjectName: "Case_Hoi_1__c", sfApiName: "eff_chng_dt__c", type: "date",
            aiPrompt: "Effective date of name change (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "legalEventDate", label: "Date of Legal Event (Marriage / Divorce / Court Order)", type: "date",
            aiPrompt: "Date of legal event such as marriage, divorce, or court order (YYYY-MM-DD, empty string if not mentioned)" },
        ],
      },
      {
        title: "New Name Information",
        fields: [
          { key: "newLegalFirstName", label: "New Legal First Name", type: "text",
            aiPrompt: "New legal first name" },
          { key: "newLegalMiddleName", label: "New Legal Middle Name", type: "text",
            aiPrompt: "New legal middle name" },
          { key: "newLegalLastName", label: "New Legal Last Name", type: "text",
            aiPrompt: "New legal last name" },
          { key: "suffix", label: "Suffix", type: "select",
            options: ["Jr", "Sr", "III", "IV", "N/A"],
            aiPrompt: "Suffix (Jr / Sr / III / IV or empty string if none)" },
        ],
      },
      {
        title: "Supporting Documentation",
        fields: [
          { key: "marriageCertificateProvided", label: "Marriage Certificate Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Marriage certificate provided? (Yes/No)" },
          { key: "divorceDecreeProvided", label: "Divorce Decree Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Divorce decree provided? (Yes/No)" },
          { key: "courtOrderProvided", label: "Court Order / Legal Name Change Document Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Court order or legal name change document provided? (Yes/No)" },
          { key: "driversLicenseProvided", label: "Updated Driver's License Provided?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Updated driver's license provided? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 14. Vehicle Change ───
  "Vehicle Change": {
    sections: [
      {
        title: "Type of Vehicle Change",
        fields: [
          { key: "vehicleChangeType", label: "Type of Change", sfObjectName: "Case", sfApiName: "WT_ChangeType__c", type: "select",
            options: ["Add Vehicle", "Remove Vehicle", "Replace Vehicle", "Update Existing Vehicle"],
            aiPrompt: "Type of vehicle change (Add Vehicle / Remove Vehicle / Replace Vehicle / Update Existing Vehicle)" },
          { key: "vehicleChangeEffectiveDate", label: "Effective Date of Change", sfObjectName: "Case_Aut_1__c", sfApiName: "chg_eff_dt__c", type: "date",
            aiPrompt: "Effective date of vehicle change (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "vehicleChangeReason", label: "Reason for Change", sfObjectName: "Case_Aut_1__c", sfApiName: "addr_chg_rsn__c", type: "select",
            options: ["Business Relocation", "Additional Location", "Correction Only", "Other"],
            aiPrompt: "Reason for vehicle change (Business Relocation / Additional Location / Correction Only / Other)" },
        ],
      },
      {
        title: "Vehicle Being Removed",
        fields: [
          { key: "removedVehicleYear", label: "Year", sfObjectName: "Case_Aut_1__c", sfApiName: "Removal_Vehicle_Year__c", type: "text",
            aiPrompt: "Year of the vehicle being removed" },
          { key: "removedVehicleMake", label: "Make", sfObjectName: "Case_Aut_1__c", sfApiName: "removal_make__c", type: "text",
            aiPrompt: "Make of the vehicle being removed" },
          { key: "removedVehicleModel", label: "Model", sfObjectName: "Case_Aut_1__c", sfApiName: "removal_model__c", type: "text",
            aiPrompt: "Model of the vehicle being removed" },
          { key: "removedVehicleVin", label: "VIN", sfObjectName: "Case_Aut_1__c", sfApiName: "Removal_Vehicle_Last_4_of_VIN__c", sfLast4: true, type: "text",
            aiPrompt: "VIN of the vehicle being removed" },
          { key: "removedLicensePlate", label: "License Plate Number", type: "text",
            aiPrompt: "License plate number of the vehicle being removed" },
          { key: "removedDateSold", label: "Date Sold / Removed", type: "date",
            aiPrompt: "Date vehicle was sold or removed (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "removalReason", label: "Reason for Removal", sfObjectName: "Case_Aut_1__c", sfApiName: "rmv_reason__c", type: "select",
            options: ["Sold", "Traded", "Totaled", "Stolen", "No Longer in Use", "Other"],
            aiPrompt: "Reason for vehicle removal (Sold / Traded / Totaled / Stolen / No Longer in Use / Other)" },
          { key: "removedBillOfSale", label: "Bill of Sale Provided?", sfObjectName: "Case", sfApiName: "Bill_of_Sale_Received__c", type: "select", sfBoolean: true,
            options: ["Yes", "No"],
            aiPrompt: "Bill of sale provided for removed vehicle? (Yes/No)" },
        ],
      },
      {
        title: "Vehicle Being Added",
        fields: [
          { key: "addedVehicleYear", label: "Year", sfObjectName: "Case_Aut_1__c", sfApiName: "year__c", type: "text", sfNumber: true,
            aiPrompt: "Year of the vehicle being added" },
          { key: "addedVehicleMake", label: "Make", sfObjectName: "Case_Aut_1__c", sfApiName: "make__c", type: "text",
            aiPrompt: "Make of the vehicle being added" },
          { key: "addedVehicleModel", label: "Model", sfObjectName: "Case_Aut_1__c", sfApiName: "model__c", type: "text",
            aiPrompt: "Model of the vehicle being added" },
          { key: "addedVehicleVin", label: "VIN (Full 17-Character)", sfObjectName: "Case_Aut_1__c", sfApiName: "vin_num__c", type: "text",
            aiPrompt: "Full 17-character VIN of the vehicle being added" },
          { key: "addedDatePurchase", label: "Date of Purchase / Acquisition", sfObjectName: "Case_Aut_1__c", sfApiName: "purch_dt__c", type: "date",
            aiPrompt: "Date of purchase or acquisition of new vehicle (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "addedPurchasePrice", label: "Purchase Price ($)", sfObjectName: "Case_Aut_1__c", sfApiName: "purch_price__c", type: "text",
            aiPrompt: "Purchase price of the new vehicle ($)" },
          { key: "addedTitleOwner", label: "Title Owner Name (Exact as on Title)", type: "text",
            aiPrompt: "Title owner name exactly as on title" },
          { key: "addedRegisteredFL", label: "Vehicle Registered in FL?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Vehicle registered in Florida? (Yes/No)" },
          { key: "addedLicensePlate", label: "License Plate Number (if known)", type: "text",
            aiPrompt: "License plate number of vehicle being added (if known)" },
        ],
      },
      {
        title: "Lienholder / Lessor Information (Added Vehicle)",
        fields: [
          { key: "lienholderName", label: "Lienholder / Lessor Name", sfObjectName: "Case_Aut_1__c", sfApiName: "lienholder__c", type: "text",
            aiPrompt: "Lienholder or lessor name" },
          { key: "lienholderLoanNumber", label: "Lienholder Loan / Account Number", sfObjectName: "Case_Aut_1__c", sfApiName: "lienholder_acc__c", type: "text",
            aiPrompt: "Lienholder loan or account number" },
          { key: "lienholderAddress", label: "Lienholder Mailing Address", sfObjectName: "Case_Aut_1__c", sfApiName: "lienholder_addr__c", type: "text",
            aiPrompt: "Lienholder mailing address" },
        ],
      },
      {
        title: "Usage / Garaging (Added Vehicle)",
        fields: [
          { key: "garagingAddress", label: "Garaging Address (Street, City, State, ZIP)", sfObjectName: "Case_Aut_1__c", sfApiName: "garaging_addr__c", type: "text",
            aiPrompt: "Garaging address (Street, City, State, ZIP)" },
          { key: "annualMileage", label: "Annual Mileage Estimate", sfObjectName: "Case_Aut_1__c", sfApiName: "ann_mileage__c", type: "text",
            aiPrompt: "Annual mileage estimate" },
          { key: "usedForRideshare", label: "Used for Rideshare (Uber/Lyft/Delivery)?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Used for rideshare like Uber, Lyft, or delivery? (Yes/No)" },
          { key: "usedForBusiness", label: "Used for Business Purposes?", sfObjectName: "Case_Aut_1__c", sfApiName: "No_use_for_business_purposes__c", type: "select", sfBoolean: true,
            options: ["Yes", "No"],
            aiPrompt: "Used for business purposes? (Yes/No)" },
        ],
      },
    ],
  },

  // ─── 15. New Boat Purchase ───
  "New Boat Purchase": {
    sections: [
      {
        title: "Existing Watercraft Policy Information",
        fields: [
          { key: "existingWatercraftPolicyNumber", label: "Existing Watercraft Policy Number", sfObjectName: "Case_Gen_1__c", sfApiName: "policy_num__c", type: "text",
            aiPrompt: "Existing watercraft policy number" },
          { key: "existingCarrier", label: "Carrier", sfObjectName: "Case_Gen_1__c", sfApiName: "carrier__c", type: "text",
            aiPrompt: "Current watercraft insurance carrier" },
          { key: "currentBoatsOnPolicy", label: "Current Boats on Policy", type: "text",
            aiPrompt: "Current boats on the policy" },
          { key: "effectiveDateToAddBoat", label: "Effective Date to Add New Boat", sfObjectName: "Case_Gen_1__c", sfApiName: "eff_date_chg__c", type: "date",
            aiPrompt: "Effective date to add new boat (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "replacingExistingBoat", label: "Replacing an Existing Boat?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Replacing an existing boat? (Yes/No)" },
          { key: "boatBeingReplaced", label: "Boat Being Replaced (Year/Make/Model/HIN)", type: "text",
            aiPrompt: "Boat being replaced (Year/Make/Model/HIN)" },
        ],
      },
      {
        title: "New Boat - Identification",
        fields: [
          { key: "boatYear", label: "Year", sfObjectName: "Case", sfApiName: "New_Boat_Year__c", type: "text", sfNumber: true,
            aiPrompt: "Year of the new boat" },
          { key: "boatManufacturer", label: "Make / Manufacturer", sfObjectName: "Case", sfApiName: "New_Boat_Make__c", type: "text",
            aiPrompt: "Make or manufacturer of the new boat" },
          { key: "boatModel", label: "Model", sfObjectName: "Case", sfApiName: "New_Boat_Model__c", type: "text",
            aiPrompt: "Model of the new boat" },
          { key: "hullIdNumber", label: "Hull Identification Number (HIN - 12 character)", sfObjectName: "Case", sfApiName: "New_VIN__c", type: "text",
            aiPrompt: "Hull identification number (HIN - 12 character)" },
          { key: "flRegistrationNumber", label: "FL Registration Number (FL # / Title #)", type: "text",
            aiPrompt: "Florida registration number (FL # or Title #)" },
          { key: "boatType", label: "Type of Boat", type: "select",
            options: ["Bowrider", "Center Console", "Cabin Cruiser", "Pontoon", "Sailboat", "Personal Watercraft", "Yacht", "Other"],
            aiPrompt: "Type of boat (Bowrider / Center Console / Cabin Cruiser / Pontoon / Sailboat / Personal Watercraft / Yacht / Other)" },
          { key: "hullMaterial", label: "Hull Material", type: "select",
            options: ["Fiberglass", "Aluminum", "Wood", "Inflatable"],
            aiPrompt: "Hull material (Fiberglass / Aluminum / Wood / Inflatable)" },
          { key: "boatLength", label: "Length (Feet, Inches)", type: "text",
            aiPrompt: "Length of the boat (feet, inches)" },
          { key: "boatPurchaseDate", label: "Date of Purchase", type: "date",
            aiPrompt: "Date of boat purchase (YYYY-MM-DD, empty string if not mentioned)" },
          { key: "boatPurchasePrice", label: "Purchase Price ($)", sfObjectName: "Case_Gen_1__c", sfApiName: "Purchase_price__c", type: "text", sfNumber: true,
            aiPrompt: "Purchase price of the boat ($)" },
          { key: "boatBillOfSale", label: "Bill of Sale Provided?", sfObjectName: "Case", sfApiName: "Bill_of_Sale_Received__c", type: "select", sfBoolean: true,
            options: ["Yes", "No"],
            aiPrompt: "Bill of sale provided for the boat? (Yes/No)" },
        ],
      },
      {
        title: "Engine / Performance",
        fields: [
          { key: "numberOfEngines", label: "Number of Engines", sfObjectName: "Case_Gen_1__c", sfApiName: "num_engines__c", type: "text",
            aiPrompt: "Number of engines" },
          { key: "engineType", label: "Engine Type", sfObjectName: "Case", sfApiName: "WT_ChangeType__c", type: "select",
            options: ["Outboard", "Inboard", "Inboard-Outboard", "Jet"],
            aiPrompt: "Engine type (Outboard / Inboard / Inboard-Outboard / Jet)" },
          { key: "engineManufacturer", label: "Engine Manufacturer", type: "text",
            aiPrompt: "Engine manufacturer" },
          { key: "engineModel", label: "Engine Model", sfObjectName: "Case", sfApiName: "New_Model__c", type: "text",
            aiPrompt: "Engine model" },
          { key: "engineYear", label: "Engine Year", sfObjectName: "Case", sfApiName: "New_Year__c", type: "text",
            aiPrompt: "Engine year" },
          { key: "totalHorsepower", label: "Total Horsepower", sfObjectName: "Case_Gen_1__c", sfApiName: "total_hp__c", type: "text",
            aiPrompt: "Total horsepower" },
          { key: "maxSpeed", label: "Maximum Speed (MPH)", type: "text",
            aiPrompt: "Maximum speed (MPH)" },
        ],
      },
      {
        title: "Trailer Information",
        fields: [
          { key: "trailerIncluded", label: "Trailer Included?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Trailer included? (Yes/No)" },
          { key: "trailerValue", label: "Trailer Value ($)", type: "text",
            aiPrompt: "Trailer value ($)" },
          { key: "trailerCoverageRequested", label: "Trailer Coverage Requested?", sfObjectName: "Case_Gen_1__c", sfApiName: "tail_cov_req__c", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Trailer coverage requested? (Yes/No)" },
        ],
      },
      {
        title: "Use / Operation",
        fields: [
          { key: "primaryUse", label: "Primary Use", type: "select",
            options: ["Pleasure", "Fishing", "Watersports", "Commercial", "Charter", "Racing"],
            aiPrompt: "Primary use of the boat (Pleasure / Fishing / Watersports / Commercial / Charter / Racing)" },
          { key: "usedForRentalCharter", label: "Used for Rental / Charter?", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Used for rental or charter? (Yes/No)" },
          { key: "navigationLimits", label: "Navigation Limits Requested", type: "text",
            aiPrompt: "Navigation limits requested" },
        ],
      },
      {
        title: "Storage / Mooring",
        fields: [
          { key: "storageLocation", label: "Storage Location When Not in Use", type: "select",
            options: ["Home", "Marina", "Dry Storage", "In Water", "Lift"],
            aiPrompt: "Storage location when not in use (Home / Marina / Dry Storage / In Water / Lift)" },
          { key: "storageAddress", label: "Storage Address", sfObjectName: "Case_Gen_1__c", sfApiName: "cur_garage_addr__c", type: "text",
            aiPrompt: "Storage address" },
          { key: "marinaName", label: "Marina Name (if applicable)", type: "text",
            aiPrompt: "Marina name (if applicable)" },
          { key: "hurricanePlan", label: "Hurricane / Storm Plan in Place?", sfObjectName: "Case_Gen_1__c", sfApiName: "Hurricane_plan__c", type: "select",
            options: ["Yes", "No"],
            aiPrompt: "Hurricane or storm plan in place? (Yes/No)" },
          { key: "hurricanePlanDescription", label: "Hurricane Plan Description", sfObjectName: "Case_Gen_1__c", sfApiName: "Hurricane_plan__c", type: "textarea",
            aiPrompt: "Hurricane plan description" },
        ],
      },
    ],
  },

  // ─── 16. Other ───
  "Other": {
    sections: [],   // no type-specific fields; only common + description
  },
};

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────

/**
 * Get all type-specific field definitions (flat array) for a given caseType.
 */
export const getFieldsForCaseType = (caseType) => {
  const config = CASE_TYPE_FIELDS[caseType];
  if (!config) return [];
  return config.sections.flatMap((s) => s.fields);
};

/**
 * Build an empty field-values object for a given caseType
 * (all type-specific keys initialised to "").
 */
export const getEmptyFieldsForCaseType = (caseType) => {
  const obj = {};
  getFieldsForCaseType(caseType).forEach((f) => { obj[f.key] = ""; });
  return obj;
};

/**
 * Build the fieldsToExtract payload sent to the backend /analyze-email.
 * Returns [{ key, aiPrompt }]
 */
export const getFieldsToExtractPayload = (caseType) => {
  return [
    ...STANDARD_HEADER_FIELDS.map((f) => ({ key: f.key, aiPrompt: f.aiPrompt })),
    ...getFieldsForCaseType(caseType).map((f) => ({ key: f.key, aiPrompt: f.aiPrompt })),
  ];
};
